import React, { PureComponent } from 'react';
import { autobind } from 'core-decorators';
import { styled, StyleSheet } from '@rexlabs/styling';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import './styles.css';
import ListingGallery from '../listing-gallery/ListingGallery';
import Map from '../map/Map';
import types from 'prop-types';
import SharedGalleryIcon from './svgs/shared/gallery.svg';
import MinimalGalleryIcon from './svgs/minimal/gallery.svg';
import SharedMapIcon from './svgs/shared/location-icon.svg';
import MinimalMapIcon from './svgs/minimal/location-icon.svg';
import SharedFloorplanIcon from './svgs/shared/floor-plan.svg';
import MinimalFloorPlanIcon from './svgs/minimal/floor-plan.svg';
import SharedVirtualTourIcon from './svgs/shared/virtual-tour.svg';
import MinimalVirtualTourIcon from './svgs/minimal/virtual-tour.svg';
import SharedVideoIcon from './svgs/shared/video.svg';
import MinimalVideoIcon from './svgs/minimal/video.svg';

const styles = StyleSheet({
  content: {
    width: '800px',
    maxWidth: '100vw',
    maxHeight: '66vh'
  },
  tabSvg: {
    width: 'auto',
    height: '1.8rem',
    fill: 'grey'
  },
  hiddenTab: {
    display: 'none'
  },
  tabList: {
    listStyleType: 'none',
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    color: 'grey',
    marginBottom: '10px'
  },

  tabItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    fontWeight: 700
  },

  versatileFont: {
    fontFamily: 'DM Sans, -apple-system, "system-ui", system-ui, sans-serif',
    fontSize: '1.4rem'
  },

  minimalFont: {
    fontFamily: 'Inter, -apple-system, "system-ui", system-ui, sans-serif',
    fontSize: '1.4rem'
  },

  classicFont: {
    fontFamily: 'Manrope, -apple-system, "system-ui", system-ui, sans-serif',
    fontSize: '1.3rem'
  }
});

const tabIndexMap = {
  images: 0,
  map: 1,
  floorplans: 2,
  videos: 3,
  virtualTours: 4
};

const VideoEmbed = ({ url }) => {
  return (
    <iframe
      width='100%'
      height='100%'
      style={{ background: 'white' }}
      src={url}
      frameBorder='0'
      allowFullScreen
    ></iframe>
  );
};

@styled(styles)
@autobind
class ListingModal extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      tabIndex: tabIndexMap[this.props.screen] ?? 0,
      display: false
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // Check if screen prop has been updated
    // If it has then change the tab
    if (this.props.screen !== prevProps.screen) {
      this.setState({
        tabIndex: tabIndexMap[this.props.screen] ?? 0
      });
    }
  }

  closeModal() {
    this.setState({ display: false });
  }

  openModal() {
    this.setState({ display: true });
  }

  renderModal() {
    const {
      theme,
      closeModal,
      floorplans,
      images,
      video,
      virtualTour,
      longitude,
      latitude,
      markerColor,
      styles: s,
      startIndex
    } = this.props;

    const GalleryIcon =
      theme === 'minimal' ? MinimalGalleryIcon : SharedGalleryIcon;
    const MapIcon = theme === 'minimal' ? MinimalMapIcon : SharedMapIcon;
    const FloorPlanIcon =
      theme === 'minimal' ? MinimalFloorPlanIcon : SharedFloorplanIcon;
    const VirtualTourIcon =
      theme === 'minimal' ? MinimalVirtualTourIcon : SharedVirtualTourIcon;
    const VideoIcon = theme === 'minimal' ? MinimalVideoIcon : SharedVideoIcon;

    let fontStyle;

    switch (theme) {
      case 'byron':
        fontStyle = 'classicFont';
        break;
      case 'minimal':
        fontStyle = 'minimalFont';
        break;
      default:
        fontStyle = 'versatileFont';
    }

    return (
      <div>
        <div className={'modal-container'}>
          <Tabs
            selectedIndex={this.state.tabIndex}
            onSelect={(tabIndex) => this.setState({ tabIndex })}
          >
            <TabList {...s('tabList')}>
              <Tab {...s('tabItem', fontStyle, { hiddenTab: !images })}>
                <GalleryIcon {...s('tabSvg')} /> Gallery
              </Tab>
              <Tab
                {...s('tabItem', fontStyle, {
                  hiddenTab: !longitude && !latitude
                })}
              >
                <MapIcon {...s('tabSvg')} /> Map
              </Tab>
              <Tab {...s('tabItem', fontStyle, { hiddenTab: !floorplans })}>
                <FloorPlanIcon {...s('tabSvg')} /> Floor Plans
              </Tab>
              <Tab {...s('tabItem', fontStyle, { hiddenTab: !video })}>
                <VideoIcon {...s('tabSvg')} /> Video
              </Tab>
              <Tab {...s('tabItem', fontStyle, { hiddenTab: !virtualTour })}>
                <VirtualTourIcon {...s('tabSvg')} /> Virtual Tour
              </Tab>
            </TabList>
            <TabPanel>
              <div {...s('content')}>
                <ListingGallery
                  thumbLocation={'bottom'}
                  images={images}
                  startIndex={startIndex}
                />
              </div>
            </TabPanel>
            <TabPanel>
              <div {...s('content')}>
                <div className={'map-container'}>
                  <Map
                    longitude={longitude}
                    latitude={latitude}
                    markerColor={markerColor}
                    height={'660px'}
                  />
                </div>
              </div>
            </TabPanel>
            <TabPanel>
              <div {...s('content')}>
                <div className={'map-container'}>
                  <ListingGallery
                    thumbLocation={'bottom'}
                    images={floorplans}
                  />
                </div>
              </div>
            </TabPanel>
            <TabPanel>
              <div {...s('content')}>
                <div className={'map-container'}>
                  <VideoEmbed url={video} />
                </div>
              </div>
            </TabPanel>
            <TabPanel>
              <div {...s('content')}>
                <div className={'map-container'}>
                  <VideoEmbed url={virtualTour} />
                </div>
              </div>
            </TabPanel>
          </Tabs>
          <div
            className={'modal-cover'}
            onClick={closeModal ?? this.closeModal}
          ></div>
        </div>
      </div>
    );
  }

  render() {
    const { label, standalone } = this.props;
    const { display } = this.state;

    if (standalone) {
      return (
        <div>
          <div
            onClick={this.openModal}
            dangerouslySetInnerHTML={{ __html: label }}
          ></div>

          {display ? this.renderModal() : ''}
        </div>
      );
    } else {
      return this.renderModal();
    }
  }
}

ListingModal.propTypes = {
  closeModal: types.func,
  screen: types.string,
  floorplans: types.array,
  images: types.array,
  video: types.string,
  virtualTour: types.string,
  standalone: types.boolean,
  label: types.string
};

ListingModal.defaultProps = {
  standalone: false
};

export default ListingModal;
window.__Components.ListingModal = ListingModal;
